import { PropsWithChildren, ReactElement, ReactNode, useEffect, useRef } from "react";

import { injectFavicon } from "../../common/helpers/injectFavIcon";
import { Nullable } from "../../common/types/types";
import { IStallMode } from "../../contexts/ConfigContext/types";

const THEME_PREFIX = "theme-";

const FAVICONS = {
	[IStallMode.SOK]: "sok/favicon.png",
	[IStallMode.HIKINIKU]: "hikiniku/favicon.png",
};

interface IConditionThemeProps {
	currentTheme?: Nullable<IStallMode>;
	children: ReactNode;
}

const ConditionTheme = ({
	currentTheme,
	children,
}: PropsWithChildren<IConditionThemeProps>): ReactElement => {
	// Track the last theme actually applied to <body> so the effect can short-circuit
	// when re-run with an unchanged value. Even with stable selectors upstream, this
	// guards against any redundant render path (parent state churn, fast-refresh, etc.)
	// re-triggering favicon injection + class toggling — the source of the refresh flicker.
	const appliedThemeRef = useRef<Nullable<IStallMode> | undefined>(undefined);

	useEffect(() => {
		if (appliedThemeRef.current === currentTheme) {
			return;
		}

		if (currentTheme) {
			injectFavicon(FAVICONS[currentTheme]);
		}

		const body = document.body;
		const allThemeClasses = Object.values(IStallMode).map((mode) => `${THEME_PREFIX}${mode}`);

		// Remove any existing theme classes
		body.classList.remove(...allThemeClasses);

		// Add the current theme class if specified
		if (currentTheme) {
			body.classList.add(`${THEME_PREFIX}${currentTheme}`);
		}

		appliedThemeRef.current = currentTheme;

		// Cleanup function to remove theme classes when component unmounts
		return () => {
			body.classList.remove(...allThemeClasses);
			appliedThemeRef.current = undefined;
		};
	}, [currentTheme]);

	const themeClass = currentTheme ? THEME_PREFIX + currentTheme : "";
	const className = `theme-wrapper ${themeClass}`.trim();

	return <div className={className}>{children}</div>;
};

export default ConditionTheme;
